/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import BigNumber from 'bignumber.js';
import { useLedgerFilteredCurrencies } from '../../../../utilities/sharedHooks/ledgerWallets';
import { SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY } from '../../../../utilities/sharedQueries';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';
import { WalletScreens } from '../../constants';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';

const FiatWithdrawalReview = ({ openScreen, options }) => {
  const { t: translate } = useTranslation();

  const { wallet, fiatAmount } = options;

  const { firstName, lastName } = useUserPersonalInfo();

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const withdrawalInfo = useMemo(() => {
    const result = {
      amount: 0,
      fee: 0,
      total: 0,
    };

    if (supportedCurrencies && supportedCurrencies[wallet?.coin]) {
      const fee =
        Math.round(fiatAmount * supportedCurrencies[wallet?.coin]?.bank_transfer_fee * 100) / 100;
      const availableBalance = wallet?.available_balance * currencies[wallet?.coin].conversion;
      const feeBigNumber = new BigNumber(fee);
      const fiatAmountBigNumber = new BigNumber(fiatAmount);
      const availableBalanceBigNumber = new BigNumber(availableBalance);
      const fiatWithFee = fiatAmountBigNumber.plus(feeBigNumber);
      const amountDeductingFee = availableBalanceBigNumber.minus(feeBigNumber);
      const withdrawAmount = fiatWithFee.isGreaterThan(availableBalanceBigNumber)
        ? amountDeductingFee
        : fiatAmountBigNumber;

      result.fee = feeBigNumber.toNumber();
      result.amount = withdrawAmount.toNumber();
      result.total = withdrawAmount.plus(feeBigNumber).toNumber();
    }

    return result;
  }, [fiatAmount, wallet, supportedCurrencies]);

  const bankFee = useMemo(() => {
    if (supportedCurrencies && supportedCurrencies[wallet?.coin]) {
      return supportedCurrencies[wallet?.coin]?.bank_transfer_fee;
    }
    return 0;
  }, [supportedCurrencies, wallet]);

  const renderFooter = () => (
    <button
      type="button"
      className="btn btn-primary py-3"
      onClick={() =>
        openScreen(WalletScreens.FIAT_WITHDRAWAL_DETAILS, {
          ...options,
          fiatAmount: withdrawalInfo.amount,
          fiatAmountWithFees: withdrawalInfo.total,
        })
      }>
      {translate('confirm_button_label')}
    </button>
  );

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => openScreen(WalletScreens.FIAT_WITHDRAWAL, { ...options })}
      title={translate('fiat_withdrawal_review_title')}
      renderFooter={renderFooter}>
      <p className="text-f6 text-shadow mt-4">
        {translate('fiat_withdrawal_review_recipient_label')}
      </p>
      <p className="text-f5 text-dark">{`${firstName} ${lastName}`}</p>
      <div className="bg-light p-4 rounded-extra mt-4">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <p className="text-f6 text-dark">{translate('withdrawal_amount_label')}</p>
          <p>{`${formatNumberWithComma(
            formatNumber(withdrawalInfo.amount, exchange.FIAT_VALUE)
          )} ${wallet?.coin?.toUpperCase()}`}</p>
        </div>
        <div className="d-flex flex-row justify-content-between align-items-center mt-4">
          <p className="text-f6 text-dark">{translate('fee_label')}</p>
          <div className="d-flex flex-column align-items-end">
            <p className="text-f6 text-dark">{`${formatNumber(
              withdrawalInfo.fee,
              exchange.FIAT_VALUE
            )} ${wallet?.coin?.toUpperCase()}`}</p>
            <p className="text-f6 text-shadow">{`${bankFee * 100}%`}</p>
          </div>
        </div>
      </div>
      <div className="bg-light p-4 rounded-extra mt-4">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <p className="text-f6 text-dark">{translate('fiat_withdrawal_total_cost')}</p>
          <p className="text-f6 text-dark">{`${formatNumberWithComma(
            formatNumber(withdrawalInfo.total, exchange.FIAT_VALUE)
          )} ${wallet?.coin?.toUpperCase()}`}</p>
        </div>
      </div>
      <div className="alert alert-info mb-4 mt-4">
        <i className="icon icon-info-icon" />
        <span className="content">{translate('fiat_withdrawal_review_note_label')}</span>
      </div>
    </ModalBodyLayout>
  );
};

export default FiatWithdrawalReview;

FiatWithdrawalReview.propTypes = {
  openScreen: PropTypes.func.isRequired,
  options: PropTypes.object,
};
FiatWithdrawalReview.defaultProps = {
  options: null,
};
